/**
 * Campaign API Service
 * 
 * Real implementation connecting to the ss-api backend campaigns endpoints.
 * Enhanced with retry logic, bulk operations, and real-time features.
 */

import { createApiService, responseTransformers, buildQueryParams, requestQueue } from './apiUtils';

// Create specialized API service for campaigns
const campaignApiService = createApiService('Campaign', '/api/campaigns');

// Create specialized API service for campaign posts (separate endpoint)
// Campaign Post API Service - standalone endpoint for post management
const campaignPostApiService = createApiService('CampaignPost', '/api/campaign-posts');

// WebSocket connection for real-time updates
let wsConnection = null;
let wsReconnectAttempts = 0;
const MAX_WS_RECONNECT_ATTEMPTS = 5;

/**
 * Initialize WebSocket connection for real-time updates
 * @param {Function} onMessage - Callback for incoming messages
 */
const initializeWebSocket = (onMessage) => {
  if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
    return wsConnection;
  }

  const token = localStorage.getItem('token');
  if (!token) return null;

  const baseUrl = campaignApiService.getBaseUrl().replace('/api/campaigns', '');
  const wsUrl = baseUrl.replace('http', 'ws') + '/ws/campaigns/';

  try {
    wsConnection = new WebSocket(`${wsUrl}?token=${token}`);

    wsConnection.onopen = () => {
      console.log('WebSocket connected');
      wsReconnectAttempts = 0;
    };

    wsConnection.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    wsConnection.onclose = () => {
      console.log('WebSocket disconnected');

      // Attempt to reconnect
      if (wsReconnectAttempts < MAX_WS_RECONNECT_ATTEMPTS) {
        wsReconnectAttempts++;
        setTimeout(() => {
          initializeWebSocket(onMessage);
        }, Math.pow(2, wsReconnectAttempts) * 1000);
      }
    };

    wsConnection.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return wsConnection;
  } catch (error) {
    console.error('Failed to initialize WebSocket:', error);
    return null;
  }
};

/**
 * Close WebSocket connection
 */
const closeWebSocket = () => {
  if (wsConnection) {
    wsConnection.close();
    wsConnection = null;
    wsReconnectAttempts = 0;
  }
};

/**
 * Transform backend campaign data to frontend format
 * @param {Object} backendCampaign - Campaign data from backend
 * @returns {Object} Transformed campaign data for frontend
 */
const transformCampaign = (backendCampaign) => {
  const camelCased = responseTransformers.toCamelCase(backendCampaign);

  return {
    ...camelCased,
    // Ensure these computed properties exist
    completionPercentage: camelCased.completionPercentage || 0,
    isActive: camelCased.isActive || false,
    platforms: camelCased.platforms || ['twitter', 'facebook', 'instagram', 'linkedin'],
    version: camelCased.version || 1,
    conflictResolution: camelCased.conflictResolution || 'latest_wins',
  };
};

/**
 * Transform backend campaign post data to frontend format
 * @param {Object} backendPost - Post data from backend
 * @returns {Object} Transformed post data for frontend
 */
const transformCampaignPost = (backendPost) => {
  const camelCased = responseTransformers.toCamelCase(backendPost);

  return {
    ...camelCased,
    campaignId: camelCased.campaign || camelCased.campaignId,
    mediaUrls: camelCased.mediaSuggestions || camelCased.mediaUrls || [],
    scheduledAt: camelCased.scheduledFor || camelCased.scheduledAt,
    platforms: [backendPost.platform],
    postType: 'campaign_post',
    sequenceName: camelCased.sequenceName || 'Campaign Sequence',
    description: camelCased.description || '',
    isCarousel: camelCased.isCarousel || false,
    carouselOrder: camelCased.carouselOrder || 1,
    version: camelCased.version || 1,
  };
};

/**
 * Helper function to extract numeric ID from post ID strings
 * @param {string|number} postId - Post ID (can be "post-31-1751144366391" or just "31")
 * @returns {string} Numeric ID as string
 */
const extractNumericPostId = (postId) => {
  if (typeof postId === 'string' && postId.startsWith('post-')) {
    const parts = postId.split('-');
    if (parts.length >= 2) {
      return parts[1]; // Extract the numeric part
    }
  }
  return String(postId); // Ensure it's a string
};

/**
 * Campaign API functions
 */
export const campaignApi = {
  /**
   * Initialize real-time connection
   * @param {Function} onMessage - Callback for real-time updates
   */
  initializeRealTime: (onMessage) => {
    return initializeWebSocket(onMessage);
  },

  /**
   * Close real-time connection
   */
  closeRealTime: () => {
    closeWebSocket();
  },

  /**
   * Analyze website to extract business information
   * @param {string} websiteUrl - URL to analyze
   * @param {boolean} forceRefresh - Force refresh bypassing cache
   * @returns {Promise} Analysis result
   */
  analyzeWebsite: async (websiteUrl, forceRefresh = false) => {
    console.log(`[Campaign API] Analyzing website: ${websiteUrl}`);

    const response = await campaignApiService.post('/analyze-website/', {
      url: websiteUrl,
      force_refresh: forceRefresh
    });

    return responseTransformers.single(response, (responseData) => {
      console.log('[Campaign API] Raw backend data:', responseData);

      // Handle nested data structure - actual data is in responseData.data
      const data = responseData.data || responseData;

      const transformedData = {
        websiteUrl: data.url || websiteUrl,
        businessData: {
          // Transform snake_case API response to camelCase frontend format
          businessName: data.business_name || '',
          description: data.description || '',
          industry: data.industry || '',
          targetAudience: data.target_audience || '',
          brandTone: data.brand_tone || 'Professional',
          products: data.products || [],
          pricingInfo: data.pricing_info || {},
          contactInfo: data.contact_info || {},
          // Keep original data for debugging
          _rawData: data
        },
        analysisId: `analysis-${Date.now()}`,
        extractedAt: new Date().toISOString(),
        source: responseData.source || data.source || 'fresh',
        cachedAt: data.cached_at,
      };
      console.log('[Campaign API] Actual data object:', data);
      console.log('[Campaign API] Transformed businessData:', transformedData.businessData);
      return transformedData;
    });
  },

  /**
   * Submit manual business information
   * @param {Object} businessData - Manually entered business data
   * @returns {Promise} Processing result
   */
  submitManualInput: async (businessData) => {
    console.log('[Campaign API] Submitting manual business input:', businessData);

    const response = await campaignApiService.post('/manual-input/', businessData);
    return responseTransformers.single(response);
  },

  /**
   * Get manual input schema for form generation
   * @returns {Promise} Schema definition
   */
  getInputSchema: async () => {
    console.log('[Campaign API] Fetching input schema');

    const response = await campaignApiService.get('/input-schema/');
    return responseTransformers.single(response, (data) => ({
      schema: data.schema,
      example: data.example
    }));
  },

  /**
   * Create a new campaign
   * @param {Object} campaignData - Campaign creation data
   * @returns {Promise} Created campaign
   */
  createCampaign: async (campaignData) => {
    console.log('[Campaign API] Creating campaign:', campaignData);

    // Transform frontend data to backend format
    const backendData = {
      name: campaignData.name || `${campaignData.businessData?.businessName || 'Campaign'} ${campaignData.campaignGoal?.replace('_', ' ') || ''}`,
      website_url: campaignData.websiteUrl,
      campaign_goal: campaignData.campaignGoal,
      description: campaignData.description || '',
      analysis_data: campaignData.businessData || {},
    };

    // Only include business_profile_id if a profile was actually selected
    // If no profile selected, omit the field entirely so backend creates new one
    if (campaignData.businessProfileId) {
      backendData.business_profile_id = campaignData.businessProfileId;
      console.log('[Campaign API] Using existing business profile ID:', campaignData.businessProfileId);
    } else {
      console.log('[Campaign API] No business profile selected, will create new one from website analysis');
    }

    console.log('[Campaign API] Sending to backend:', backendData);
    const response = await campaignApiService.post('/', backendData);

    return responseTransformers.single(response, (data) => ({
      campaign: transformCampaign(data.campaign || data),
      posts: [] // Posts will be generated asynchronously
    }));
  },

  /**
   * Get user's business profiles for selection UIs (campaigns, settings, etc.)
   * @deprecated Use businessProfileApi.getProfilesForSelection() instead
   * @returns {Promise} Business profiles list
   */
  getBusinessProfilesForSelection: async () => {
    console.log('[Campaign API] Fetching business profiles for selection (delegating to businessProfileApi)');

    // Delegate to the dedicated business profile API for consistency
    const { businessProfileApi } = await import('./businessProfileApi');
    return businessProfileApi.getProfilesForSelection();
  },

  /**
   * Get campaign details
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Campaign details
   */
  getCampaign: async (campaignId) => {
    console.log(`[Campaign API] Fetching campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/`);
    return responseTransformers.single(response, transformCampaign);
  },

  /**
   * Update campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} updates - Fields to update
   * @param {boolean} autoSave - Whether this is an auto-save operation
   * @returns {Promise} Updated campaign
   */
  updateCampaign: async (campaignId, updates, autoSave = false) => {
    console.log(`[Campaign API] Updating campaign ${campaignId}:`, updates);

    const updateRequest = async () => {
      // Transform frontend updates to backend format
      const backendUpdates = {};

      if (updates.name) backendUpdates.name = updates.name;
      if (updates.description) backendUpdates.description = updates.description;
      if (updates.campaignGoal) backendUpdates.campaign_goal = updates.campaignGoal;
      if (updates.status) backendUpdates.status = updates.status;
      if (updates.version) backendUpdates.version = updates.version;

      const response = await campaignApiService.patch(`/${campaignId}/`, backendUpdates, {
        headers: {
          'X-Auto-Save': autoSave ? 'true' : 'false',
        },
      });

      return responseTransformers.single(response, transformCampaign);
    };

    if (autoSave) {
      // Queue auto-save requests
      requestQueue.add(updateRequest, 3);
      return { success: true, autoSave: true };
    } else {
      return await updateRequest();
    }
  },

  /**
   * Delete campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Deletion result
   */
  deleteCampaign: async (campaignId) => {
    console.log(`[Campaign API] Deleting campaign: ${campaignId}`);

    await campaignApiService.delete(`/${campaignId}/`);
    return { success: true };
  },

  /**
   * Get posts for a campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Campaign posts
   */
  getCampaignPosts: async (campaignId) => {
    console.log(`[Campaign API] Fetching posts for campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/posts/`);
    return responseTransformers.list(response, transformCampaignPost);
  },

  /**
   * Update a campaign post
   * @param {string} postId - Post ID
   * @param {Object} updates - Post updates
   * @param {boolean} autoSave - Whether this is an auto-save operation
   * @returns {Promise} Updated post
   */
  updateCampaignPost: async (postId, updates, autoSave = false) => {
    console.log(`[Campaign API] Updating post ${postId}:`, updates);

    const updateRequest = async () => {
      const backendUpdates = {};

      if (updates.content) backendUpdates.content = updates.content;
      if (updates.scheduledAt) backendUpdates.scheduled_for = updates.scheduledAt;
      if (updates.status) backendUpdates.status = updates.status;
      if (updates.platform) backendUpdates.platform = updates.platform;
      if (updates.mediaUrls) backendUpdates.media_suggestions = updates.mediaUrls;
      if (updates.version) backendUpdates.version = updates.version;

      const numericId = extractNumericPostId(postId);
      const response = await campaignPostApiService.patch(`/${numericId}/`, backendUpdates, {
        headers: {
          'X-Auto-Save': autoSave ? 'true' : 'false',
        },
      });

      return responseTransformers.single(response, transformCampaignPost);
    };

    if (autoSave) {
      // Queue auto-save requests
      requestQueue.add(updateRequest, 4);
      return { success: true, autoSave: true };
    } else {
      try {
        return await updateRequest();
      } catch (error) {
        console.error('Failed to update campaign post:', error);
        // Return optimistic update for now
        return {
          success: true,
          data: {
            id: postId,
            ...updates,
            updatedAt: new Date().toISOString()
          }
        };
      }
    }
  },

  /**
   * Bulk update campaign posts
   * @param {string} campaignId - Campaign ID
   * @param {Array} updates - Array of post updates [{id, ...updates}]
   * @returns {Promise} Updated posts
   */
  bulkUpdatePosts: async (campaignId, updates) => {
    console.log(`[Campaign API] Bulk updating posts for campaign ${campaignId}:`, updates);

    const response = await campaignApiService.post(`/${campaignId}/posts/bulk-update/`, { updates });
    return responseTransformers.single(response, (data) =>
      data.posts ? data.posts.map(transformCampaignPost) : []
    );
  },

  /**
   * Bulk schedule campaign posts
   * @param {string} campaignId - Campaign ID
   * @param {Array} schedules - Array of schedule data [{postId, scheduledAt}]
   * @returns {Promise} Scheduled posts
   */
  bulkSchedulePosts: async (campaignId, schedules) => {
    console.log(`[Campaign API] Bulk scheduling posts for campaign ${campaignId}:`, schedules);

    const response = await campaignApiService.post(`/${campaignId}/posts/bulk-schedule/`, { schedules });
    return responseTransformers.single(response, (data) =>
      data.posts ? data.posts.map(transformCampaignPost) : []
    );
  },

  /**
   * Get campaign analytics
   * @param {string} campaignId - Campaign ID
   * @param {Object} options - Analytics options (timeRange, metrics, etc.)
   * @returns {Promise} Campaign analytics
   */
  getCampaignAnalytics: async (campaignId, options = {}) => {
    console.log(`[Campaign API] Fetching analytics for campaign ${campaignId}:`, options);

    const response = await campaignApiService.get(`/${campaignId}/analytics/`, {
      time_range: options.timeRange,
      metrics: options.metrics?.join(','),
      platform: options.platform,
    });

    return responseTransformers.single(response);
  },

  /**
   * Export campaign data
   * @param {string} campaignId - Campaign ID
   * @param {string} format - Export format ('csv', 'pdf', 'xlsx')
   * @param {Object} options - Export options
   * @returns {Promise} Export result
   */
  exportCampaign: async (campaignId, format = 'csv', options = {}) => {
    console.log(`[Campaign API] Exporting campaign ${campaignId} as ${format}:`, options);

    const response = await campaignApiService.post(`/${campaignId}/export/`, { format, ...options });
    return responseTransformers.single(response);
  },

  /**
   * Check for campaign conflicts
   * @param {string} campaignId - Campaign ID
   * @param {number} currentVersion - Current version number
   * @returns {Promise} Conflict check result
   */
  checkConflicts: async (campaignId, currentVersion) => {
    console.log(`[Campaign API] Checking conflicts for campaign ${campaignId}, version ${currentVersion}`);

    const response = await campaignApiService.get(`/${campaignId}/conflicts/`, { version: currentVersion });
    return responseTransformers.single(response, (data) => ({
      hasConflicts: data.has_conflicts || false,
      conflicts: data.conflicts || [],
      latestVersion: data.latest_version || currentVersion,
    }));
  },

  /**
   * Resolve campaign conflicts
   * @param {string} campaignId - Campaign ID
   * @param {string} resolution - Resolution strategy ('merge', 'force_local', 'force_remote')
   * @param {Object} resolutionData - Resolution data
   * @returns {Promise} Resolution result
   */
  resolveConflicts: async (campaignId, resolution, resolutionData = {}) => {
    console.log(`[Campaign API] Resolving conflicts for campaign ${campaignId}:`, { resolution, resolutionData });

    const response = await campaignApiService.post(`/${campaignId}/resolve-conflicts/`, {
      resolution,
      resolution_data: resolutionData,
    });

    return responseTransformers.single(response, (data) => transformCampaign(data.campaign));
  },

  /**
   * List user campaigns with filtering
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Campaigns list
   */
  listCampaigns: async (filters = {}) => {
    console.log('[Campaign API] Listing campaigns with filters:', filters);

    const response = await campaignApiService.get('/', {
      status: filters.status !== 'all' ? filters.status : undefined,
      campaign_goal: filters.goal !== 'all' ? filters.goal : undefined,
      search: filters.search,
      page: filters.page,
      page_size: filters.page_size,
    });

    return responseTransformers.paginated(response, transformCampaign);
  },

  /**
   * Get user quota information
   * @returns {Promise} Quota details
   */
  getUserQuota: async () => {
    console.log('[Campaign API] Fetching user quota');

    const response = await campaignApiService.get('/quota/');
    return responseTransformers.single(response);
  },

  /**
   * Get user's cached website analyses
   * @returns {Promise} Cached analyses
   */
  getCachedAnalyses: async () => {
    console.log('[Campaign API] Fetching cached analyses');

    const response = await campaignApiService.get('/cached-analyses/');
    return responseTransformers.list(response);
  },

  /**
   * Get ICP (Ideal Customer Profile) analysis for campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} ICP analysis data
   */
  getICPAnalysis: async (campaignId) => {
    console.log(`[Campaign API] Fetching ICP analysis for campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/icp-analysis/`);
    return responseTransformers.single(response);
  },

  /**
   * Regenerate ICP analysis for campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Regeneration result
   */
  regenerateICP: async (campaignId) => {
    console.log(`[Campaign API] Regenerating ICP analysis for campaign: ${campaignId}`);

    const response = await campaignApiService.post(`/${campaignId}/regenerate_icp/`);
    return responseTransformers.single(response);
  },

  /**
   * Get content sequences for campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Content sequences
   */
  getContentSequences: async (campaignId) => {
    console.log(`[Campaign API] Fetching content sequences for campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/sequences/`);
    return responseTransformers.list(response);
  },

  /**
   * Approve content sequence
   * @param {string} campaignId - Campaign ID
   * @param {string} sequenceId - Sequence ID
   * @returns {Promise} Approval result
   */
  approveSequence: async (campaignId, sequenceId) => {
    console.log(`[Campaign API] Approving sequence ${sequenceId} for campaign: ${campaignId}`);

    const response = await campaignApiService.post(`/${campaignId}/sequences/${sequenceId}/approve/`);
    return responseTransformers.single(response);
  },

  /**
   * Create a content sequence (client-side only)
   * Note: Backend generates sequences dynamically from posts, so this is a frontend concept
   * @param {string} campaignId - Campaign ID
   * @param {Object} sequenceData - Sequence creation data
   * @returns {Promise} Created sequence
   */
  createSequence: async (campaignId, sequenceData) => {
    console.log(`[Campaign API] Creating sequence for campaign ${campaignId}:`, sequenceData);

    const response = await campaignApiService.post(`/${campaignId}/create_sequence/`, {
      name: sequenceData.name,
      description: sequenceData.description || '',
      objective: sequenceData.objective || '',
      timing: sequenceData.timing || '',
      frequency: sequenceData.frequency || '',
      tags: sequenceData.tags || [],
      metadata: sequenceData.metadata || {}
    });

    return responseTransformers.single(response);
  },

  /**
   * Add a post to a sequence
   * @param {string} campaignId - Campaign ID
   * @param {string} sequenceId - Sequence ID
   * @param {Object} postData - Post data to add
   * @returns {Promise} Added post
   */
  addPostToSequence: async (campaignId, sequenceId, postData) => {
    console.log(`[Campaign API] Adding post to sequence ${sequenceId} in campaign ${campaignId}:`, postData);

    // Transform frontend post data to backend format
    const backendPostData = {
      campaign: parseInt(campaignId, 10),
      sequence_id: parseInt(sequenceId, 10), // Use sequence_id field for the new model
      platform: postData.platform || postData.platforms?.[0] || 'twitter',
      content: postData.content || '',
      media_suggestions: postData.mediaUrls || postData.media_suggestions || [],
      status: postData.status || 'draft',
      // order_in_sequence will be auto-calculated by backend
    };

    // Add scheduling info if provided
    if (postData.scheduledAt || postData.scheduled_for) {
      backendPostData.scheduled_for = postData.scheduledAt || postData.scheduled_for;
    }

    // Use the campaign posts endpoint
    const response = await campaignPostApiService.post('/', backendPostData);
    return responseTransformers.single(response, transformCampaignPost);
  },

  /**
   * Reorder posts in a sequence
   * @param {string} campaignId - Campaign ID
   * @param {string} sequenceId - Sequence ID
   * @param {Array} postOrder - Array of post IDs in new order
   * @returns {Promise} Reorder result
   */
  reorderSequencePosts: async (campaignId, sequenceId, postOrder) => {
    console.log(`[Campaign API] Reordering posts in sequence ${sequenceId} for campaign ${campaignId}:`, postOrder);

    const response = await campaignApiService.put(`/${campaignId}/sequences/${sequenceId}/reorder/`, {
      post_order: postOrder
    });
    return responseTransformers.single(response);
  },

  /**
   * Delete a sequence (client-side only)
   * @param {string} campaignId - Campaign ID
   * @param {string} sequenceId - Sequence ID
   * @returns {Promise} Deletion result
   */
  deleteSequence: async (campaignId, sequenceId) => {
    console.log(`[Campaign API] Deleting sequence ${sequenceId} from campaign ${campaignId}`);

    // Since sequences are generated from posts, deleting a sequence means
    // removing the sequence metadata from all its posts
    // For now, return success since this is a frontend concept
    return Promise.resolve({
      success: true,
      message: 'Sequence deleted successfully'
    });
  },

  /**
   * Bulk approve campaign posts
   * @param {string} campaignId - Campaign ID
   * @param {Array} postIds - Array of post IDs to approve/unapprove
   * @param {boolean} approved - Whether to approve (true) or unapprove (false) the posts
   * @param {string} approvalNotes - Optional approval notes
   * @returns {Promise} Bulk approval result
   */
  bulkApprovePosts: async (campaignId, postIds, approved = true, approvalNotes = '') => {
    console.log(`[Campaign API] Bulk ${approved ? 'approving' : 'unapproving'} posts for campaign ${campaignId}:`, postIds);

    // Extract numeric IDs from strings like "post-31-1751144366391"
    const numericPostIds = postIds.map(postId => {
      return parseInt(extractNumericPostId(postId), 10);
    }).filter(id => !isNaN(id)); // Filter out any invalid IDs

    // Note: DRF action URLs use underscores, not hyphens
    const response = await campaignApiService.post(`/${campaignId}/bulk_approve/`, { 
      post_ids: numericPostIds,
      approved,
      approval_notes: approvalNotes
    });
    return responseTransformers.single(response);
  },

  /**
   * Get campaign approval summary
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Approval summary stats
   */
  getApprovalSummary: async (campaignId) => {
    console.log(`[Campaign API] Fetching approval summary for campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/approval_summary/`);
    return responseTransformers.single(response);
  },

  /**
   * List campaign assets
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Campaign assets
   */
  getCampaignAssets: async (campaignId) => {
    console.log(`[Campaign API] Fetching assets for campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/assets/`);
    return responseTransformers.list(response);
  },

  /**
   * Upload asset to campaign
   * @param {string} campaignId - Campaign ID
   * @param {File} file - File to upload
   * @param {Object} metadata - Asset metadata
   * @returns {Promise} Upload result
   */
  uploadAsset: async (campaignId, file, metadata = {}) => {
    console.log(`[Campaign API] Uploading asset for campaign: ${campaignId}`);

    const formData = new FormData();
    formData.append('file', file);

    // Add metadata
    Object.keys(metadata).forEach(key => {
      formData.append(key, metadata[key]);
    });

    const response = await campaignApiService.upload(`/${campaignId}/upload_asset/`, formData);
    return responseTransformers.single(response);
  },

  /**
   * Get brand assets for campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Brand assets
   */
  getBrandAssets: async (campaignId) => {
    console.log(`[Campaign API] Fetching brand assets for campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/brand_assets/`);
    return responseTransformers.list(response);
  },

  /**
   * Schedule campaign posts
   * @param {string} campaignId - Campaign ID
   * @param {Object} scheduleData - Scheduling configuration
   * @returns {Promise} Scheduling result
   */
  scheduleCampaign: async (campaignId, scheduleData) => {
    console.log(`[Campaign API] Scheduling campaign ${campaignId}:`, scheduleData);

    const response = await campaignApiService.post(`/${campaignId}/schedule/`, scheduleData);
    return responseTransformers.single(response);
  },

  /**
   * Submit feedback for campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} feedback - Feedback data
   * @returns {Promise} Feedback submission result
   */
  submitFeedback: async (campaignId, feedback) => {
    console.log(`[Campaign API] Submitting feedback for campaign ${campaignId}:`, feedback);

    const response = await campaignApiService.post(`/${campaignId}/feedback/`, feedback);
    return responseTransformers.single(response);
  },

  /**
   * Trigger campaign regeneration
   * @param {string} campaignId - Campaign ID
   * @param {Object} regenerationOptions - Options for regeneration
   * @returns {Promise} Regeneration result
   */
  regenerateCampaign: async (campaignId, regenerationOptions = {}) => {
    console.log(`[Campaign API] Regenerating campaign ${campaignId}:`, regenerationOptions);

    const response = await campaignApiService.post(`/${campaignId}/regenerate/`, regenerationOptions);
    return responseTransformers.single(response);
  },

  /**
   * Get campaign version history
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} Version history
   */
  getVersionHistory: async (campaignId) => {
    console.log(`[Campaign API] Fetching version history for campaign: ${campaignId}`);

    const response = await campaignApiService.get(`/${campaignId}/versions/`);
    return responseTransformers.list(response);
  },

  // Standalone Campaign Posts Management

  /**
   * List standalone campaign posts
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Campaign posts list
   */
  listCampaignPosts: async (filters = {}) => {
    console.log('[Campaign API] Listing campaign posts with filters:', filters);

    const response = await campaignPostApiService.get('/', {
      campaign: filters.campaign,
      platform: filters.platform,
      status: filters.status,
      search: filters.search,
      page: filters.page,
    });

    return responseTransformers.paginated(response, transformCampaignPost);
  },

  /**
   * Create a post for a specific campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} postData - Post creation data
   * @returns {Promise} Created post
   */
  createCampaignPostForCampaign: async (campaignId, postData) => {
    console.log(`[Campaign API] Creating post for campaign ${campaignId}:`, postData);

    // If sequence_order is not provided, calculate it
    let enhancedPostData = {
      ...postData,
      campaign: campaignId
    };

    if (!postData.sequence_order && !postData.sequenceOrder) {
      try {
        const existingPosts = await campaignApi.getCampaignPosts(campaignId);
        if (existingPosts && existingPosts.data && existingPosts.data.length > 0) {
          const maxOrder = Math.max(...existingPosts.data.map(p => p.sequenceOrder || 0));
          enhancedPostData.sequence_order = maxOrder + 1;
        } else {
          enhancedPostData.sequence_order = 1;
        }
      } catch (error) {
        console.warn('Could not get existing posts for sequence order:', error);
        enhancedPostData.sequence_order = 1;
      }
    }

    return campaignApi.createCampaignPost(enhancedPostData);
  },

  /**
   * Create standalone campaign post
   * @param {Object} postData - Post creation data
   * @returns {Promise} Created post
   */
  createCampaignPost: async (postData) => {
    console.log('[Campaign API] Creating campaign post:', postData);

    // Transform frontend data to backend format
    const backendData = {
      campaign: postData.campaign ? parseInt(postData.campaign, 10) : undefined,
      platform: postData.platform || postData.platforms?.[0] || 'twitter',
      content: postData.content || '',
      media_suggestions: postData.mediaUrls || postData.media_suggestions || [],
      status: postData.status || 'draft',
      sequence_order: postData.sequence_order || postData.sequenceOrder || 1,
    };

    // Add optional fields if present
    if (postData.scheduled_for || postData.scheduledAt) {
      backendData.scheduled_for = postData.scheduled_for || postData.scheduledAt;
    }

    if (postData.post_type || postData.postType) {
      backendData.post_type = postData.post_type || postData.postType;
    }

    if (postData.ai_personalized_sections || postData.metadata) {
      backendData.ai_personalized_sections = {
        ...postData.ai_personalized_sections,
        ...postData.metadata
      };
    }

    const response = await campaignPostApiService.post('/', backendData);
    return responseTransformers.single(response, transformCampaignPost);
  },

  /**
   * Get standalone campaign post details
   * @param {string} postId - Post ID
   * @returns {Promise} Post details
   */
  getCampaignPost: async (postId) => {
    console.log(`[Campaign API] Fetching campaign post: ${postId}`);

    const numericId = extractNumericPostId(postId);
    const response = await campaignPostApiService.get(`/${numericId}/`);
    return responseTransformers.single(response, transformCampaignPost);
  },

  /**
   * Restore deleted campaign post
   * @param {string} postId - Post ID
   * @returns {Promise} Restore result
   */
  restoreCampaignPost: async (postId) => {
    console.log(`[Campaign API] Restoring campaign post: ${postId}`);

    const numericId = extractNumericPostId(postId);
    const response = await campaignPostApiService.post(`/${numericId}/restore/`);
    return responseTransformers.single(response, transformCampaignPost);
  },

  /**
   * Toggle campaign post approval
   * @param {string} postId - Post ID
   * @param {boolean} approved - Whether to approve or unapprove the post
   * @param {string} approvalNotes - Optional approval notes
   * @returns {Promise} Approval result
   */
  togglePostApproval: async (postId, approved = true, approvalNotes = '') => {
    console.log(`[Campaign API] ${approved ? 'Approving' : 'Unapproving'} post: ${postId}`);

    const numericId = extractNumericPostId(postId);
    const response = await campaignPostApiService.post(`/${numericId}/approve/`, {
      approved,
      approval_notes: approvalNotes
    });
    return responseTransformers.single(response, transformCampaignPost);
  },

  /**
   * Regenerate campaign post content
   * @param {string} postId - Post ID
   * @param {Object} regenerationOptions - Options for regeneration
   * @returns {Promise} Regeneration result
   */
  regeneratePost: async (postId, regenerationOptions = {}) => {
    console.log(`[Campaign API] Regenerating post ${postId}:`, regenerationOptions);

    const numericId = extractNumericPostId(postId);
    const response = await campaignPostApiService.post(`/${numericId}/regenerate/`, regenerationOptions);
    return responseTransformers.single(response, transformCampaignPost);
  },

  // Content Performance Intelligence Integration
  // Uses analytics API endpoints for predictions

  /**
   * Generate content performance prediction
   * @param {Object} content - Content to analyze
   * @returns {Promise} Prediction result
   */
  generateContentPrediction: async (content) => {
    console.log('[Campaign API] Generating content prediction:', content);

    // Use analytics API service for predictions
    const analyticsService = createApiService('Analytics', '/api/analytics');
    const response = await analyticsService.post('/predict/', content);
    return responseTransformers.single(response);
  },

  /**
   * Get content predictions list
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Predictions list
   */
  getContentPredictions: async (filters = {}) => {
    console.log('[Campaign API] Fetching content predictions:', filters);

    const analyticsService = createApiService('Analytics', '/api/analytics');
    const response = await analyticsService.get('/predictions/', {
      campaign: filters.campaign,
      platform: filters.platform,
      confidence_min: filters.confidence_min,
      page: filters.page,
    });

    return responseTransformers.single(response);
  },

  /**
   * Get specific content prediction details
   * @param {string} predictionId - Prediction ID
   * @returns {Promise} Prediction details
   */
  getContentPrediction: async (predictionId) => {
    console.log(`[Campaign API] Fetching content prediction: ${predictionId}`);

    const analyticsService = createApiService('Analytics', '/api/analytics');
    const response = await analyticsService.get(`/predictions/${predictionId}/`);
    return responseTransformers.single(response);
  },

  /**
   * Validate content prediction against actual performance
   * @param {string} predictionId - Prediction ID
   * @param {Object} actualData - Actual performance data
   * @returns {Promise} Validation result
   */
  validateContentPrediction: async (predictionId, actualData) => {
    console.log(`[Campaign API] Validating prediction ${predictionId}:`, actualData);

    const analyticsService = createApiService('Analytics', '/api/analytics');
    const response = await analyticsService.post(`/predictions/${predictionId}/validate/`, actualData);
    return responseTransformers.single(response);
  },

  /**
   * Rate content prediction quality
   * @param {string} predictionId - Prediction ID
   * @param {Object} rating - Rating data (1-5 stars, feedback)
   * @returns {Promise} Rating result
   */
  rateContentPrediction: async (predictionId, rating) => {
    console.log(`[Campaign API] Rating prediction ${predictionId}:`, rating);

    const analyticsService = createApiService('Analytics', '/api/analytics');
    const response = await analyticsService.post(`/predictions/${predictionId}/rate/`, rating);
    return responseTransformers.single(response);
  },

  /**
   * Get AI insights for content optimization
   * @param {Object} filters - Filter parameters
   * @returns {Promise} AI insights
   */
  getAIInsights: async (filters = {}) => {
    console.log('[Campaign API] Fetching AI insights:', filters);

    const analyticsService = createApiService('Analytics', '/api/analytics');
    const response = await analyticsService.get('/intelligence/insights/', {
      campaign: filters.campaign,
      insight_type: filters.insight_type,
      timeframe: filters.timeframe,
    });

    return responseTransformers.single(response);
  },

  /**
   * Trigger AI pattern analysis
   * @param {Object} analysisOptions - Analysis configuration
   * @returns {Promise} Analysis result
   */
  triggerPatternAnalysis: async (analysisOptions = {}) => {
    console.log('[Campaign API] Triggering pattern analysis:', analysisOptions);

    const analyticsService = createApiService('Analytics', '/api/analytics');
    const response = await analyticsService.post('/intelligence/analyze/', analysisOptions);
    return responseTransformers.single(response);
  },

  // Utility functions for request management
  utils: {
    /**
     * Clear the request queue
     */
    clearQueue: () => {
      requestQueue.clear();
    },

    /**
     * Get queue status
     */
    getQueueStatus: () => {
      return requestQueue.getStatus();
    },

    /**
     * Force process queue
     */
    forceProcessQueue: () => {
      requestQueue.process();
    },
  },
};

/**
 * Analytics tracking function
 * @param {string} eventName - Event name
 * @param {Object} properties - Event properties
 */
export const trackEvent = (eventName, properties = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Campaign Analytics] ${eventName}:`, properties);
  }

  // TODO: Integrate with actual analytics service
  // This could send events to Google Analytics, Mixpanel, etc.
};

export default campaignApi; 